import * as XLSX from 'xlsx';

const splitCategoryPath = (value) =>
  String(value || '')
    .split(/\s*(?:>|\/|\||,)\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

export const parseExcelProductRows = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
    throw new Error('Excel file must have at least header and data rows');
  }

  let headers = [];
  let subHeaders = [];
  let mainHeaderMap = {};
  let currentMainHeader = 'General';
  let dataStartIndex = 2;

  // Detect if Row 0 is grouped headers (Product Overview, Attributes, Inventory, etc.) or direct subheaders
  const row0Tokens = rawData[0].map((c) =>
    c && typeof c === 'string' ? c.trim().toLowerCase() : ''
  );
  const isGroupedHeader = row0Tokens.some(
    (t) =>
      t.includes('product overview') ||
      t.includes('inventory') ||
      t.includes('quantity table') ||
      t.includes('attributes')
  );

  if (isGroupedHeader) {
    rawData[0].forEach((cell, index) => {
      if (cell && typeof cell === 'string' && cell.trim()) {
        currentMainHeader = cell.trim();
      }
      headers[index] = currentMainHeader;
      mainHeaderMap[index] = currentMainHeader;
    });

    rawData[1].forEach((cell, index) => {
      subHeaders[index] =
        cell && typeof cell === 'string' ? cell.trim() : `Column_${index + 1}`;
    });
    dataStartIndex = 2;
  } else {
    // Single header row
    rawData[0].forEach((cell, index) => {
      const sub =
        cell && typeof cell === 'string' ? cell.trim() : `Column_${index + 1}`;
      subHeaders[index] = sub;
      const subLower = sub.toLowerCase();
      if (
        /productnumber|part\s*number|part\s*no|mpn|datasheet|description|manufacturer|lead\s*time/i.test(
          subLower
        )
      ) {
        mainHeaderMap[index] = 'Product Overview';
      } else if (
        /qty|stock|available|sku|multiplier|moq|shortlead/i.test(subLower)
      ) {
        mainHeaderMap[index] = 'Inventory';
      } else if (/price|rate|break/i.test(subLower)) {
        mainHeaderMap[index] = 'Quantity Table';
      } else if (/image|video|photo|featured/i.test(subLower)) {
        mainHeaderMap[index] = 'Assets';
      } else if (/rohs/i.test(subLower)) {
        mainHeaderMap[index] = 'RoHS';
      } else if (/cat/i.test(subLower)) {
        mainHeaderMap[index] = 'Categories';
      } else if (/alternate/i.test(subLower)) {
        mainHeaderMap[index] = 'AlternateProducts';
      } else {
        mainHeaderMap[index] = 'Attributes';
      }
    });
    dataStartIndex = 1;
  }

  const prodNumIdx = subHeaders.findIndex((h) =>
    /productNumber|part\s*number|part\s*no|mpn|item\s*code/i.test(h)
  );
  const descIdx = subHeaders.findIndex((h) => /description|desc/i.test(h));
  const mfrIdx = subHeaders.findIndex((h) =>
    /manufacturer|make|brand|mfr/i.test(h)
  );
  const urlHeaderIdx = subHeaders.findIndex((h) => /datasheet/i.test(h));

  function smartAlignRow(rawRow) {
    if (!rawRow || !Array.isArray(rawRow)) return null;
    const nonEmpties = rawRow.filter(
      (x) =>
        x !== undefined &&
        x !== null &&
        String(x).trim() !== '' &&
        String(x).trim() !== '0'
    );
    if (nonEmpties.length === 0) return null; // Skip empty row

    let row = [...rawRow];

    if (urlHeaderIdx !== -1) {
      if (
        row[urlHeaderIdx + 1] &&
        String(row[urlHeaderIdx + 1]).trim().startsWith('http')
      ) {
        row = row.slice(1);
      } else if (
        row[urlHeaderIdx + 2] &&
        String(row[urlHeaderIdx + 2]).trim().startsWith('http')
      ) {
        row = row.slice(2);
      }
    }

    if (
      (row[0] === undefined || row[0] === null || String(row[0]).trim() === '') &&
      row[1] !== undefined &&
      row[2] !== undefined
    ) {
      if (
        /^[0-9]+$/.test(String(row[1]).trim()) &&
        typeof row[2] === 'string' &&
        row[2].trim().length > 1
      ) {
        row = row.slice(1);
      }
    }

    return row;
  }

  const dataRows = rawData.slice(dataStartIndex);
  let products = [];
  let currentProduct = null;

  dataRows.forEach((rawRow) => {
    const row = smartAlignRow(rawRow);
    if (!row) return;

    const serialNumber =
      row[0] !== undefined && row[0] !== null ? String(row[0]).trim() : '';
    const productNumberVal =
      prodNumIdx !== -1 && row[prodNumIdx] !== undefined && row[prodNumIdx] !== null
        ? String(row[prodNumIdx]).trim()
        : '';
    const descVal =
      descIdx !== -1 && row[descIdx] !== undefined && row[descIdx] !== null
        ? String(row[descIdx]).trim()
        : '';
    const mfrVal =
      mfrIdx !== -1 && row[mfrIdx] !== undefined && row[mfrIdx] !== null
        ? String(row[mfrIdx]).trim()
        : '';

    const isNewProduct =
      (serialNumber &&
        (!currentProduct || currentProduct.serialNumber !== serialNumber)) ||
      (productNumberVal &&
        (!currentProduct ||
          (currentProduct.productOverview?.productNumber?.[0] !== productNumberVal &&
            !currentProduct.serialNumber))) ||
      (!currentProduct && (descVal || mfrVal));

    if (isNewProduct || !currentProduct) {
      if (currentProduct) {
        products.push(currentProduct);
      }

      currentProduct = {
        serialNumber: serialNumber || String(products.length + 1),
        productOverview: {},
        attributes: {},
        inventory: {
          qtyAvailable: 0,
          minimumMultiplier: 1,
        },
        quantityTable: [],
      };
    }

    let quantityTableEntry = {};

    row.forEach((cell, colIndex) => {
      const mainHeader = mainHeaderMap[colIndex] || 'General';
      const subHeader = subHeaders[colIndex];
      const value =
        cell !== undefined && cell !== null ? String(cell).trim() : '';

      if (!subHeader || !value) return;

      if (mainHeader === 'Product Overview') {
        if (!currentProduct.productOverview[subHeader]) {
          currentProduct.productOverview[subHeader] = [];
        }

        if (/productnumber|part\s*number|part\s*no|mpn/i.test(subHeader)) {
          currentProduct.productOverview['ElectrowizCode'] = [value];
        }
        currentProduct.productOverview[subHeader].push(value);
      } else if (mainHeader === 'Inventory') {
        if (/qtyavailable|quantity|stock/i.test(subHeader)) {
          const num = Number(value.replace(/[^0-9.-]/g, ''));
          currentProduct.inventory[subHeader] = isNaN(num) ? 0 : num;
        } else if (/minimummultiplier|minmultiplier|moq/i.test(subHeader)) {
          const num = Number(value.replace(/[^0-9.-]/g, ''));
          currentProduct.inventory[subHeader] = isNaN(num) ? 1 : num;
        } else {
          currentProduct.inventory[subHeader] = value;
        }
      } else if (mainHeader === 'Quantity Table') {
        let num = Number(value.replace(/[^0-9.-]/g, ''));
        quantityTableEntry[subHeader] = isNaN(num) ? value : num;
      } else if (mainHeader === 'Assets') {
        if (!currentProduct.attributes['Assets']) {
          currentProduct.attributes['Assets'] = {
            image: [],
            video: [],
            featuredImage: [],
          };
        }
        if (/^image\d*$/i.test(subHeader)) {
          const urls = value
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
          currentProduct.attributes['Assets'].image.push(...urls);
        } else if (/video/i.test(subHeader)) {
          currentProduct.attributes['Assets'].video.push(value);
        } else if (/featured/i.test(subHeader)) {
          currentProduct.attributes['Assets'].featuredImage.push(value);
        } else {
          if (!currentProduct.attributes['Assets'][subHeader]) {
            currentProduct.attributes['Assets'][subHeader] = [];
          }
          currentProduct.attributes['Assets'][subHeader].push(value);
        }
      } else {
        if (!currentProduct.attributes[mainHeader]) {
          currentProduct.attributes[mainHeader] = {};
        }
        if (!currentProduct.attributes[mainHeader][subHeader]) {
          currentProduct.attributes[mainHeader][subHeader] = [];
        }
        currentProduct.attributes[mainHeader][subHeader].push(value);
      }
    });

    if (Object.keys(quantityTableEntry).length > 0) {
      currentProduct.quantityTable.push(quantityTableEntry);
    }
  });

  if (currentProduct) {
    products.push(currentProduct);
  }

  // Format each product into finalResult
  let finalResult = [];

  for (let m = 0; m < products.length; m++) {
    let product = products[m];
    let index = m;

    let categoryName = [];
    if (product?.attributes) {
      Object.keys(product.attributes).forEach((mainKey) => {
        if (!mainKey.toLowerCase().includes('cat')) return;
        Object.values(product.attributes[mainKey]).forEach((vals) => {
          const values = Array.isArray(vals) ? vals : [vals];
          values.forEach((value) => {
            if (typeof value === 'string') {
              categoryName.push(...splitCategoryPath(value));
            } else if (value) {
              categoryName.push(String(value).trim());
            }
          });
        });
      });
    }

    categoryName = [
      ...new Set(
        categoryName.map((name) => String(name).trim()).filter(Boolean)
      ),
    ];
    if (categoryName.length === 0 && product?.productOverview) {
      Object.keys(product.productOverview).forEach((key) => {
        if (key.toLowerCase().includes('cat')) {
          const vals = product.productOverview[key];
          if (Array.isArray(vals)) {
            vals.forEach((v) => {
              if (typeof v === 'string') {
                categoryName.push(...splitCategoryPath(v));
              } else if (v) {
                categoryName.push(String(v).trim());
              }
            });
          } else if (typeof vals === 'string' && vals.trim()) {
            categoryName.push(...splitCategoryPath(vals));
          }
        }
      });
    }
    if (categoryName.length === 0) {
      const fallbackCats =
        product?.attributes?.Categories?.categories ||
        product?.attributes?.Category?.category ||
        [];
      if (Array.isArray(fallbackCats)) {
        fallbackCats.forEach((v) => {
          if (typeof v === 'string') {
            categoryName.push(...splitCategoryPath(v));
          } else if (v) {
            categoryName.push(String(v).trim());
          }
        });
      } else if (typeof fallbackCats === 'string' && fallbackCats.trim()) {
        categoryName.push(...splitCategoryPath(fallbackCats));
      }
    }

    let manufacturerName =
      product?.productOverview?.manufacturer?.[0] || '';
    let attributesObj = product?.attributes?.Attributes || {};
    if (
      !attributesObj ||
      typeof attributesObj !== 'object' ||
      Object.keys(attributesObj).length === 0
    ) {
      attributesObj = {};
      if (product?.attributes) {
        Object.keys(product.attributes).forEach((key) => {
          if (
            ![
              'Assets',
              'Categories',
              'Category',
              'AlternateProducts',
              'RoHS',
              'AdditionalResources',
              'GST',
              'Status',
              'Serial No',
            ].includes(key)
          ) {
            if (
              typeof product.attributes[key] === 'object' &&
              product.attributes[key] !== null
            ) {
              // The serial number column (group left blank in the
              // "Quick Create Format" sheet) is never an attribute.
              const { 'Serial No': _serialNo, ...groupValues } = product.attributes[key];
              Object.assign(attributesObj, groupValues);
            }
          }
        });
      }
    }

    if (manufacturerName) {
      const trimmedMan =
        typeof manufacturerName === 'string'
          ? manufacturerName.trim()
          : String(manufacturerName).trim();
      if (!attributesObj['Manufacturer']) {
        attributesObj['Manufacturer'] = [trimmedMan];
      }
    }

    let rohsRaw =
      product?.attributes?.RoHS?.RoHS || product?.RoHS || [];
    let rohsStr = Array.isArray(rohsRaw) ? rohsRaw[0] : rohsRaw;
    let rohsValue = /no|non|false|0/i.test(String(rohsStr || ''))
      ? 'NO'
      : 'YES';

    // Optional "GST" / "Status" column groups (written by the Quick List export).
    // Files without them keep the backend defaults.
    const gstRaw = product?.attributes?.GST?.gst?.[0];
    const statusRaw = product?.attributes?.Status?.status?.[0];
    const extraFields = {};
    if (gstRaw !== undefined && gstRaw !== '' && !isNaN(Number(gstRaw))) {
      extraFields.gst = Number(gstRaw);
    }
    if (statusRaw) {
      extraFields.status = String(statusRaw).trim().toUpperCase();
    }

    finalResult.push({
      serialNo: index + 1,
      "Serial No": index + 1,
      productOverview: {
        ElectrowizCode:
          product?.productOverview?.ElectrowizCode ||
          (product?.productOverview?.productNumber
            ? [product.productOverview.productNumber[0]]
            : []),
        productNumber: product?.productOverview?.productNumber || [],
        datasheetUrl: product?.productOverview?.datasheetUrl || [],
        description: product?.productOverview?.description || [],
        manufacturer:
          product?.productOverview?.manufacturer ||
          (manufacturerName ? [manufacturerName] : []),
        manufactureLeadTime:
          product?.productOverview?.manufactureLeadTime || [],
      },
      attributes: attributesObj,
      categories: categoryName.length > 0 ? categoryName : ['General'],
      alternateProducts:
        product?.attributes?.AlternateProducts?.alternateProducts || [],
      RoHS: [rohsValue],
      additionalResources: product?.attributes?.AdditionalResources || {},
      assets: {
        image: product?.attributes?.Assets?.image || [],
        video: product?.attributes?.Assets?.video || [],
        featuredImage:
          product?.attributes?.Assets?.featuredImage ||
          (product?.attributes?.Assets?.image?.[0]
            ? [product.attributes.Assets.image[0]]
            : []),
      },
      inventory: product?.inventory || {
        qtyAvailable: 0,
        minimumMultiplier: 1,
      },
      quantityTable: product?.quantityTable || [],
      ...extraFields,
    });
  }

  return finalResult;
};

export const parseExcelFileLocally = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  return parseExcelProductRows(rawData);
};
